import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { ContextChangeController } from '../ns-services/context-change.js';
import { nsDirectoryServiceContext, nsDirectoryStateContext } from '../ns-services/contexts.js';

import { ButtressDbService, buttressDbServiceContext } from '@buttress/crag';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';

import { NSDirectoryService } from '../ns-services/ns-directory-service.js';

import Person from '../models/person.js';
import { APP_LOCALE } from '../helpers/format.js';

import '../ns-elements/ns-mnemonic-verification.js';
import '../ns-elements/ns-company-switcher.js';

@customElement('ns-top-bar')
export class NSTopBar extends LitElement {
  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: nsDirectoryServiceContext })
  private __dir?: NSDirectoryService;

  private __profileChanges = new ContextChangeController(this, nsDirectoryStateContext, async () => {
    await this.__profileChange();
  });

  private __person?: Person;

  private __clockTimer?: ReturnType<typeof setInterval>;

  @property()
  heading: string = '';

  @property({ type: Boolean })
  greeting: boolean = false;

  @state()
  private __time: string = '';

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    css`
      :host {
        display: block;
      }

      .title-wrapper {
        overflow: hidden;
      }
      .title {
        margin: 0;
        /* Figma's 60px is on a 1920px frame; scale with the viewport so the greeting fits narrower screens */
        font-size: clamp(var(--wa-font-size-2xl), 3.125vw, var(--wa-font-size-5xl));
        font-weight: 500;
        line-height: 100%;
        letter-spacing: -0.09375rem;

        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      #top-app-bar {
        color: var(--ns-top-bar-color, var(--wa-color-text-normal));
        margin-bottom: var(--wa-space-m);
      }

      /* stretch so the clock is as tall as the company switcher */
      .actions {
        align-self: center;
        align-items: stretch;
        gap: var(--wa-space-xs);
      }

      .time {
        display: flex;
        align-items: center;

        padding: 0 var(--wa-space-s);

        background: var(--wa-color-surface-default);
        color: var(--wa-color-text-normal);
        border-radius: var(--ns-card-border-radius, 0.5em);

        font-size: var(--wa-font-size-xl);
        font-weight: 300;
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__db) throw new Error('Unable to find db service');

    if (!this.__dir) throw new Error('Directory service not found');
    await this.__dir.initialised;

    this.__updateClock();
    this.__clockTimer = setInterval(() => this.__updateClock(), 1000);

    await this.__profileChange();
  }

  disconnectedCallback() {
    clearInterval(this.__clockTimer);
    super.disconnectedCallback();
  }

  private async __profileChange() {
    if (!this.__dir) throw new Error('Can not find directory service');

    this.__person = this.__dir.me;
    this.requestUpdate();
  }

  private __updateClock() {
    this.__time = new Date().toLocaleTimeString(APP_LOCALE, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  protected override render() {
    const title =
      this.greeting && this.__person?.forename
        ? `Hi ${this.__person.forename}, let's see what's on the cards`
        : this.heading;
    return html`
      <div role="banner" id="top-app-bar" class="layout horizontal">
        <div class="layout horizontal center flex title-wrapper">
          <h1 class="title flex">${title}</h1>
        </div>
        <div class="actions layout horizontal end-justified">
          <ns-company-switcher></ns-company-switcher>
          <time class="time">${this.__time}</time>
          <ns-mnemonic-verification></ns-mnemonic-verification>
        </div>
      </div>
    `;
  }
}
